// Interface untuk crawl results data sesuai dengan struktur JSON
export interface CrawlData {
  url: string
  title: string
  word_counts: Record<string, number>
  total_words_found: number
  crawl_success: boolean
  error?: string
}

export interface WhoisData {
  domain: string
  registrar: string | null
  owner: string | null
  creation_date: string | null
  expiration_date: string | null
  name_servers: string[]
  status: string[] | null
}

export interface CrawlResultItem {
  query: string
  query_type: string
  domain: string
  url: string
  crawl_data: CrawlData
  whois_data: WhoisData
  processed_at: string
}

export interface CrawlResultsData {
  total_websites: number
  search_words: string[]
  total_queries: number
  results: CrawlResultItem[]
  generated_at: string
}

// Interface untuk data yang digunakan di UI
export interface ReportItem {
  id: string
  platform: string
  accountUrl: string
  accountName: string
  detectedKeywords: string[]
  confidence: number
  detectedAt: string
  status: "pending" | "verified" | "rejected"
  content?: string
  metadata?: {
    followers?: number
    posts?: number
    verified?: boolean
    [key: string]: any
  }
}

// Load data from JSON file
export async function loadCrawlResults(): Promise<CrawlResultsData> {
  try {
    const response = await fetch('/data/crawl_results.json')
    if (!response.ok) {
      throw new Error('Failed to load crawl results')
    }
    const data: CrawlResultsData = await response.json()
    return data
  } catch (error) {
    console.warn('Could not load crawl_results.json, using mock data:', error)
    // Return mock data if file doesn't exist
    return getMockCrawlResults()
  }
}

// Convert CrawlResultItem to ReportItem for UI
export function convertToReportItem(item: CrawlResultItem, index: number): ReportItem {
  // Extract keywords from word_counts
  const detectedKeywords = Object.keys(item.crawl_data.word_counts).filter(
    key => item.crawl_data.word_counts[key] > 0
  )
  
  // Calculate confidence based on total_words_found (normalize to 0-1)
  const confidence = Math.min(item.crawl_data.total_words_found / 100, 1)
  
  // Determine platform from domain
  const getPlatform = (domain: string): string => {
    if (domain.includes('instagram.com')) return 'Instagram'
    if (domain.includes('facebook.com')) return 'Facebook'
    if (domain.includes('tiktok.com')) return 'TikTok'
    if (domain.includes('twitter.com') || domain.includes('x.com')) return 'Twitter'
    if (domain.includes('youtube.com')) return 'YouTube'
    return 'Website'
  }
  
  return {
    id: `report-${index + 1}`,
    platform: getPlatform(item.domain),
    accountUrl: item.url,
    accountName: item.domain,
    detectedKeywords,
    confidence,
    detectedAt: new Date(item.processed_at).toLocaleString('id-ID'),
    status: "pending",
    content: item.crawl_data.title || item.crawl_data.url,
    metadata: {
      verified: item.whois_data.owner !== null && item.whois_data.owner !== 'Registration Private',
    }
  }
}

function getMockCrawlResults(): CrawlResultsData {
  return {
    total_websites: 23,
    search_words: ["slot", "casino", "togel"],
    total_queries: 23,
    generated_at: new Date().toISOString(),
    results: Array.from({ length: 23 }, (_, i) => ({
      query: ["slot", "casino", "togel"][i % 3],
      query_type: "1-word",
      domain: `example${i + 1}.com`,
      url: `https://example${i + 1}.com`,
      crawl_data: {
        url: `https://example${i + 1}.com`,
        title: `Sample Title ${i + 1}`,
        word_counts: { slot: 5, casino: 3 },
        total_words_found: 8,
        crawl_success: true,
      },
      whois_data: {
        domain: `example${i + 1}.com`,
        registrar: "GoDaddy.com, LLC",
        owner: "Registration Private",
        creation_date: "2019-01-01 00:00:00",
        expiration_date: "2026-01-01 00:00:00",
        name_servers: ["ns1.example.com"],
        status: ["clientTransferProhibited"],
      },
      processed_at: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
    }))
  }
}

